/**
 * Which units may the public website PRICE from?
 *
 * The site used to derive the investment summary, finance calculator,
 * "PRICED FROM" figures and every "from R…" price from the cheapest unit
 * REGARDLESS of status — so sold stock kept headlining pages. Only units
 * with status 'available' are actually for sale ('available' is the sole
 * for-sale state: cancelled deals reset units back to it; the other 22
 * statuses are held / in-progress / sold).
 *
 * Policy (2026-08-06): price off available units; when a development has
 * ZERO available units, silently fall back to all non-deleted priced
 * units (page renders exactly as before — deliberately NO "sold out"
 * messaging anywhere).
 *
 * Generic over a structural unit shape so both full `Unit` objects and
 * the narrow `{ price }` shapes in seo.ts work; status is compared to the
 * string literal 'available' (this app only imports TYPES from the admin
 * app's src/, and importing the UnitStatus enum VALUE would cross the
 * build boundary).
 */

export interface PriceableUnitLike {
  price?: number;
  status?: string;
  isDeleted?: boolean;
}

export function isUnitSaleable<T extends PriceableUnitLike>(u: T): boolean {
  return !u.isDeleted && (u.price ?? 0) > 0 && u.status === 'available';
}

export function getSaleableUnits<T extends PriceableUnitLike>(
  units: T[] | undefined | null,
): T[] {
  return (units || []).filter(isUnitSaleable);
}

/**
 * Available-first with silent fallback: saleable units when any exist,
 * otherwise all non-deleted priced units (never an empty downgrade).
 */
export function getPricingUnits<T extends PriceableUnitLike>(
  units: T[] | undefined | null,
): T[] {
  const saleable = getSaleableUnits(units);
  if (saleable.length > 0) return saleable;
  return (units || []).filter((u) => !u.isDeleted && (u.price ?? 0) > 0);
}

/** Cheapest price across the pricing units; null when none. */
export function getFromPrice(
  units: PriceableUnitLike[] | undefined | null,
): number | null {
  const pool = getPricingUnits(units);
  if (pool.length === 0) return null;
  return Math.min(...pool.map((u) => u.price ?? 0));
}
